import { useMutation } from '@apollo/client';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildDocumentInput,
  UpdateDocumentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateDocumentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateDocumentTRPC } from './useUpdateDocumentTRPC';

export const useUpdateDocument = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateDocumentGraphQL, graphqlState] = useMutation(
    UpdateDocumentDocument,
    {
      update: (cache) => {
        evictField(cache, 'document');
      },
      refetchQueries: [namedOperations.Query.getDocumentById],
    }
  );

  const trpcMutation = useUpdateDocumentTRPC();

  const updateDocument = async (
    variables: UpdateChildDocumentInput
  ): Promise<UpdateDocumentMutation> => {
    if (trpcEnabled) {
      const { schedule, ...rest } = variables;

      return trpcMutation.updateDocument({
        ...rest,
        ...mapScheduleToTRPC(schedule),
      });
    }

    const result = await updateDocumentGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update document');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateDocument,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDocument,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
